import {
  Clock,
  AlertCircle,
  CreditCard,
  Copy,
  CheckCircle,
  ArrowLeft,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { useSwapStatus } from '@/hooks/useCpn'
import { CpnSwapStatusResponse, CpnBeneficiaryUser } from '@/types/cpn'
import { ATB_TRANSFER_STATUS, TransferData } from '@/types/payment'
import { getPaymentStatusInfo } from '@/utils/cpn'

interface PaymentStatusScreenProps {
  transferData: TransferData
  paymentId?: string
  beneficiary?: CpnBeneficiaryUser
  onBack: () => void
  onStartNewTransfer: () => void
}

const PaymentStatusScreen = ({
  transferData,
  paymentId,
  beneficiary,
  onBack,
  onStartNewTransfer,
}: PaymentStatusScreenProps) => {
  const { toast } = useToast()
  const currentPaymentId =
    paymentId || sessionStorage.getItem('currentPaymentId') || ''
  const currentIntentId = sessionStorage.getItem('currentIntentId') || ''

  // Use TanStack Query hook for swap status with smart polling
  const {
    data: swapStatusDataRaw,
    isLoading,
    error,
  } = useSwapStatus(currentIntentId, !!currentIntentId)

  // Cast to proper type
  const swapStatusData = swapStatusDataRaw as CpnSwapStatusResponse | undefined
  // const retryPaymentMutation = useRetryPayment();

  // Extract payment data from swap-status response structure
  const cpnPayment = swapStatusData?.cpnPayment as
    | {
        fromAmount: number
        fromCurrency: string
        toAmount: number
        toCurrency: string
        status: string
        createdAt: string
        paymentUuid: string
        feeAmount: number
        feeCurrency: string
      }
    | undefined

  // Map swap-status response to expected payment structure
  const paymentInfo = cpnPayment
    ? {
        fromAmount: cpnPayment.fromAmount.toString(),
        fromCurrency: cpnPayment.fromCurrency,
        toAmount: cpnPayment.toAmount.toString(),
        toCurrency: cpnPayment.toCurrency,
        status:
          cpnPayment.status || (swapStatusData?.transactionStatus as string),
        createdAt: cpnPayment.createdAt,
        id: cpnPayment.paymentUuid,
        feeAmount: cpnPayment.feeAmount.toString(),
        feeCurrency: cpnPayment.feeCurrency,
      }
    : null

  // Extract RFIs from swap-status response
  const rfis = swapStatusData?.rfis || []

  const statusInfo = getPaymentStatusInfo(paymentInfo?.status as string)

  // Helper function to determine ATB status
  const getAtbStatus = () => {
    return swapStatusData?.atbTransfers?.[0]?.status || null
  }

  // Helper functions for ATB status checks
  const isAtbAvailable = () => getAtbStatus() === ATB_TRANSFER_STATUS.AVAILABLE
  const isAtbProcessing = () => {
    const status = getAtbStatus()
    return (
      status === ATB_TRANSFER_STATUS.PROCESSING_EMT ||
      status === ATB_TRANSFER_STATUS.PROCESSING_RMT ||
      status === ATB_TRANSFER_STATUS.FULFILLED
    )
  }

  // Display payment UUID from swap-status response, fallback to intent UUID
  const displayId = swapStatusData?.paymentUuid || currentIntentId
  const idLabel = swapStatusData?.paymentUuid ? 'Payment ID' : 'Intent ID'

  const handleCopyId = () => {
    navigator.clipboard.writeText(displayId)
    toast({
      title: 'Copied',
      description: `${idLabel} copied to clipboard`,
    })
  }

  // const handleRetryPayment = async () => {
  //   try {
  //     await retryPaymentMutation.mutateAsync({ paymentId: currentPaymentId });
  //     toast({
  //       title: "Payment retried",
  //       description: "Payment has been resubmitted for processing",
  //     });
  //   } catch (error) {
  //     toast({
  //       title: "Retry failed",
  //       description: "Failed to retry payment. Please try again.",
  //       variant: "destructive",
  //     });
  //   }
  // };

  if (isLoading) {
    return (
      <div className="h-[600px] flex flex-col">
        <div className="p-4 space-y-3 flex-1 flex flex-col justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Loading payment status...
          </p>
        </div>
      </div>
    )
  }

  if (error || !paymentInfo) {
    return (
      <div className="h-[600px] flex flex-col">
        <div className="p-4 space-y-3 flex-1 flex flex-col justify-center items-center">
          <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load payment status
          </p>
          <Button onClick={onBack} variant="outline" size="sm">
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  // Check for "Too many active transactions" error
  const errorMessage =
    error instanceof Error ? error.message : String(error || '')
  const hasActiveTransactionsError = errorMessage
    .toLowerCase()
    .includes('too many active transactions')

  return (
    <div className="h-[600px] flex flex-col">
      <div className="flex-1 overflow-y-auto">
        {/* Show error message at top for "Too many active transactions" */}
        {hasActiveTransactionsError ? (
          <div className="relative p-6 text-white rounded-t-3xl bg-gradient-to-br from-red-500 to-red-600">
            {/* Back Button - Top Left */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="absolute top-4 left-4 text-white/80 hover:text-white hover:bg-white/10 p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                <span>Payment Error</span>
              </div>
            </div>

            <div className="text-center space-y-3">
              <h2 className="text-2xl font-bold">
                Too many active transactions
              </h2>
              <p className="text-sm opacity-90">
                Please complete or cancel existing transactions before
                continuing
              </p>
              <div className="bg-white/10 rounded-lg p-3 mt-4">
                <p className="text-xs flex items-start">
                  <span className="mr-2">💡</span>
                  Try completing or canceling your existing transactions first,
                  then retry this payment.
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Normal Professional Header */
          <div
            className={`relative p-6 text-white rounded-t-3xl bg-gradient-to-br ${statusInfo.gradient}`}
          >
            {/* Back Button - Top Left */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="absolute top-4 left-4 text-white/80 hover:text-white hover:bg-white/10 p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {/* Status Badge */}
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium flex items-center">
                <statusInfo.Icon className="w-4 h-4 mr-2" />
                <span>{statusInfo.label}</span>
              </div>
            </div>

            {/* Main Amount Display */}
            <div className="text-center">
              <div className="text-sm opacity-80 mb-1">You Send</div>
              <div className="text-4xl font-bold tracking-tight">
                {/* Show CAD amount from ATB transfer if received, otherwise show original CAD amount from transferData */}
                {swapStatusData?.atbTransfers &&
                swapStatusData.atbTransfers[0]?.amount
                  ? `${swapStatusData.atbTransfers[0].amount} ${swapStatusData.atbTransfers[0].currency || 'CAD'}`
                  : `${transferData.sendAmount} ${transferData.sendCurrency}`}
              </div>
            </div>

            <div className="flex items-center justify-center my-3">
              <div className="w-full h-px bg-white/20"></div>
            </div>

            <div className="text-center">
              <div className="text-sm opacity-80 mb-1">
                {paymentInfo?.status === 'COMPLETED'
                  ? beneficiary
                    ? `${beneficiary.firstName.charAt(0).toUpperCase() + beneficiary.firstName.slice(1)} Received`
                    : 'They Received'
                  : beneficiary
                    ? `${beneficiary.firstName.charAt(0).toUpperCase() + beneficiary.firstName.slice(1)} Will Receive`
                    : 'They Will Receive'}
              </div>
              <div className="text-2xl font-semibold">
                {paymentInfo.toAmount} {paymentInfo.toCurrency}
              </div>
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* E-transfer Instructions - show when status is Available */}
          {isAtbAvailable() && (
            <Card className="p-4 border-blue-200 dark:border-green-700 bg-blue-50 dark:bg-green-900/20">
              <h3 className="font-semibold text-green-800 dark:text-blue-200 mb-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Accept Money Transfer (30 mins)
              </h3>
              <div className="space-y-3">
                <p className="text-sm ">
                  Click below to accept the money transfer of{' '}
                  <strong>
                    {transferData.sendAmount} {transferData.sendCurrency}
                  </strong>{' '}
                  to complete your transaction.
                </p>
                <div className="flex items-center justify-center pt-4">
                  <div className="w-full flex justify-center">
                    <a
                      className="bg-black text-white dark:bg-gray-800 dark:text-white py-3 px-6 border-2 border-green dark:border-green-700 w-full flex items-center justify-center text-center font-semibold rounded-lg hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
                      href={`https://etransfer.interac.ca/reqPayment/eml/${swapStatusData.atbStatus}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="mr-2">Accept Money Transfer</span>
                      <img
                        src={'/interac.png'}
                        alt="Interac Logo"
                        className="inline-block ml-2"
                        style={{ width: '64px', height: '24px' }}
                        data-testid="img-order-status-interac-logo"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Current Status */}
          <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
              Current Status
            </h3>
            <div className="flex items-center space-x-3">
              <div
                className={`w-2 h-2 rounded-full ${
                  paymentInfo?.status === 'COMPLETED'
                    ? 'bg-green-500 dark:bg-green-400'
                    : isAtbAvailable()
                      ? 'bg-yellow-500 dark:bg-yellow-400'
                      : isAtbProcessing()
                        ? 'bg-blue-500 dark:bg-blue-400'
                        : 'bg-gray-300 dark:bg-gray-600'
                }`}
              ></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {paymentInfo?.status === 'COMPLETED'
                    ? 'Funds Delivered'
                    : isAtbAvailable()
                      ? 'Money Transfer Pending'
                      : isAtbProcessing()
                        ? 'Payment Processing'
                        : 'Payment Initiated'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {paymentInfo?.status === 'COMPLETED'
                    ? 'Funds received by beneficiary'
                    : isAtbAvailable()
                      ? 'Waiting for you to accept the money transfer...'
                      : isAtbProcessing()
                        ? 'Processing your funds...'
                        : paymentInfo.createdAt
                          ? new Date(paymentInfo.createdAt).toLocaleString()
                          : 'N/A'}
                </p>
              </div>
            </div>
          </Card>

          {/* Payment Details Card */}
          <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <CreditCard className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
              Transaction Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {idLabel}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
                    {displayId.slice(0, 8)}...
                  </span>
                  <Button
                    onClick={handleCopyId}
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Payment Method
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {transferData.paymentMethod}
                </span>
              </div>
              {/* <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Fee</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {paymentInfo?.feeAmount} {paymentInfo?.feeCurrency}
              </span>
            </div> */}
              {/* <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Estimated Delivery</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {transferData.deliveryDate}
              </span>
            </div> */}
            </div>
          </Card>

          {/* Show processing message when money transfer is being processed */}
          {isAtbProcessing() && paymentInfo?.status !== 'COMPLETED' && (
            <Card className="p-4 border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
              <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Money Transfer Received - Processing Payment
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-green-700 dark:text-green-300">
                  We've received your money transfer and are now processing your
                  payment. No further action is required.
                </p>
                {swapStatusData.atbTransfers[0]?.amount && (
                  <div className="bg-green-100 dark:bg-green-800/20 rounded-lg p-2 mt-2">
                    <p className="text-xs text-green-600 dark:text-green-400">
                      ✅ Received:{' '}
                      {String(swapStatusData.atbTransfers[0].amount)}{' '}
                      {String(swapStatusData.atbTransfers[0].currency || 'CAD')}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Show completion message when payment is COMPLETED */}
          {paymentInfo?.status === 'COMPLETED' && (
            <Card className="p-4 border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
              <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Payment Completed Successfully! 🎉
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-green-700 dark:text-green-300">
                  Your payment has been successfully delivered to the recipient.
                </p>
                {swapStatusData?.atbTransfers &&
                  swapStatusData.atbTransfers[0]?.amount && (
                    <div className="bg-green-100 dark:bg-green-800/20 rounded-lg p-2 mt-2">
                      <p className="text-xs text-green-600 dark:text-green-400">
                        ✅ Final Amount: {paymentInfo.toAmount}{' '}
                        {paymentInfo.toCurrency}
                      </p>
                    </div>
                  )}
              </div>
            </Card>
          )}

          {/* Missing Information - Red blocking message */}
          {rfis.length > 0 && (
            <Card className="p-4 border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
              <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Missing Information
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                Some information is missing to complete this payment. Please
                provide the missing information to proceed.
              </p>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            {/* {paymentInfo.status?.toLowerCase() === 'failed' && (
            <Button
              onClick={handleRetryPayment}
              disabled={retryPaymentMutation.isPending}
              className="w-full flex items-center justify-center space-x-2 h-12 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {retryPaymentMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span>Retry Payment</span>
            </Button>
          )} */}

            {/* Only show "Start New Transfer" when payment is completed or failed, not when user needs to take action */}
            {(paymentInfo?.status === 'COMPLETED' ||
              paymentInfo.status?.toLowerCase() === 'failed') && (
              <Button
                onClick={onStartNewTransfer}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white h-12 font-semibold"
              >
                Start New Transfer
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Secured by Paytrie • Circle Payments Network
          </p>
        </div>
      </div>
    </div>
  )
}

export default PaymentStatusScreen
